import React, { useState, useEffect } from 'react';
import { Mail, Download, Calendar, Users } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSignups = () => {
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSignups();
  }, []);

  const fetchSignups = async () => {
    try {
      const response = await axios.get(`${API}/signups`);
      setSignups(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching signups:', error);
      toast.error('Failed to load signups');
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Email', 'Date', 'Time'];
    const rows = signups.map(signup => {
      const date = new Date(signup.timestamp);
      return [
        signup.email,
        date.toLocaleDateString(),
        date.toLocaleTimeString()
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `email-signups-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('CSV exported successfully');
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div className="admin-title-section">
          <h1 className="admin-title">Email Signups</h1>
          <p className="admin-subtitle">Manage your landing page email subscriptions</p>
        </div>
        <Button onClick={exportToCSV} className="export-button" disabled={signups.length === 0}>
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      <div className="stats-grid">
        <Card className="stat-card">
          <div className="stat-icon">
            <Users className="w-6 h-6" />
          </div>
          <div className="stat-content">
            <p className="stat-label">Total Signups</p>
            <p className="stat-value">{signups.length}</p>
          </div>
        </Card>

        <Card className="stat-card">
          <div className="stat-icon">
            <Calendar className="w-6 h-6" />
          </div>
          <div className="stat-content">
            <p className="stat-label">Latest Signup</p>
            <p className="stat-value">
              {signups.length > 0 
                ? new Date(signups[signups.length - 1].timestamp).toLocaleDateString()
                : 'N/A'
              }
            </p>
          </div>
        </Card>
      </div>

      <Card className="signups-card">
        <div className="signups-header">
          <h2 className="signups-title">All Signups</h2>
        </div>
        
        {loading ? (
          <div className="loading-state">
            <p>Loading signups...</p>
          </div>
        ) : signups.length === 0 ? (
          <div className="empty-state">
            <Mail className="w-12 h-12 mb-4 text-gray-400" />
            <p className="empty-text">No signups yet</p>
            <p className="empty-subtext">Email signups will appear here</p>
          </div>
        ) : (
          <div className="signups-list">
            {signups.map((signup) => (
              <div key={signup.id} className="signup-item">
                <div className="signup-email">
                  <Mail className="w-4 h-4" />
                  <span>{signup.email}</span>
                </div>
                <div className="signup-date">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(signup.timestamp)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdminSignups;
